import React from "react";
import { toast } from "react-toastify";
import {
  Button,
  DynamicDropdown,
  StaticDropdown,
  TextInput,
  TimePicker,
} from "src/Components";
import { IExecutionSettings } from "src/Interfaces/goldArbitrage";
import {
  getCashAssetDropdown,
  getExecutionSettings,
  setExecutionSettings,
} from "src/Requests/goldArbitrage";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface IProps {
  onClose: () => void;
}

const getTimePickerValue = (value?: string | null) => {
  if (!value) return value;
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
    const [hours, ...rest] = value.split(":");
    return `1970-01-01T${hours.padStart(2, "0")}:${rest.join(":")}`;
  }
  return value;
};

const Settings: React.FC<IProps> = ({ onClose }) => {
  const [config, setConfig] = React.useState({} as IExecutionSettings);

  React.useEffect(() => {
    getExecutionSettings().then(setConfig).catch(packetErrorHandler);
  }, []);

  const changeHandler = (name: string, value: string | number) => {
    setConfig({
      ...config,
      [name]: value,
    });
  };

  const numericChangeHandler = (name: string, value: string) => {
    setConfig({
      ...config,
      [name]: Number(value),
    });
  };

  const timeChangeHandler = (
    name: string,
    value: { timeString: string; ISOString: string | null },
  ) => {
    setConfig((current) => ({
      ...current,
      [name]: value.timeString || null,
    }));
  };

  const submitHandler = () => {
    if (!config.startTime || !config.endTime) {
      toast.error("ساعت شروع و پایان اجرا را وارد کنید.");
      return;
    }

    setExecutionSettings(config)
      .then((resp) => {
        if (!resp.data) {
          toast.error(resp.msg);
          return;
        }
        toast.success(resp.msg);
        onClose();
      })
      .catch(packetErrorHandler);
  };

  return (
    <div className="w-[360px] max-w-full flex flex-col gap-3 px-2 py-4">
      <TimePicker
        name="startTime"
        placeholder="زمان شروع"
        value={getTimePickerValue(config.startTime)}
        onChange={timeChangeHandler}
        extraLabel
      />
      <TimePicker
        name="endTime"
        placeholder="زمان پایان"
        value={getTimePickerValue(config.endTime)}
        onChange={timeChangeHandler}
        extraLabel
      />
      <StaticDropdown
        dropdown={{
          name: "navSource",
          placeholder: "منبع NAV در نمایش و اجرا",
          extraLabel: true,
        }}
        options={[
          { id: "Tse", name: "NAV TSE" },
          { id: "Calculated", name: "NAV محاسباتی" },
          { id: "Calculated2", name: "NAV محاسباتی 2" },
        ]}
        value={config.navSource || "Tse"}
        onChange={changeHandler}
      />
      <TextInput
        label="حداکثر ارزش بلوک"
        name="maxBlockValue"
        value={config.maxBlockValue?.toString() || ""}
        onChange={numericChangeHandler}
        kind="number"
        extraLabel
      />
      <div className="grid grid-cols-2 gap-2 w-full">
        <TextInput
          label="آستانه حداکثر مانده حساب ریالی"
          name="cleanupOffset"
          value={config.cleanupOffset?.toString() ?? ""}
          onChange={numericChangeHandler}
          kind="price"
          extraLabel
        />
        <TextInput
          label="مانده هدف پس از پاکسازی"
          name="minCleanupValue"
          value={config.minCleanupValue?.toString() ?? ""}
          onChange={numericChangeHandler}
          kind="price"
          extraLabel
        />
      </div>
      <TextInput
        label="٪ آسان گرفتن اختلاف حباب در ارسال اول"
        name="firstStageBubbleThreshold"
        value={config.firstStageBubbleThreshold?.toString() || ""}
        onChange={changeHandler}
        kind="percent"
        extraLabel
      />
      <TextInput
        label="مدت انتظار اول(ثانیه)"
        name="firstWaitSeconds"
        value={config.firstWaitSeconds?.toString() || ""}
        onChange={numericChangeHandler}
        kind="number"
        extraLabel
      />
      <TextInput
        label="٪ آسان گرفتن اختلاف حباب در ارسال دوم"
        name="secondStageBubbleThreshold"
        value={config.secondStageBubbleThreshold?.toString() || ""}
        onChange={changeHandler}
        kind="percent"
        extraLabel
      />
      <TextInput
        label="مدت انتظار دوم(ثانیه)"
        name="secondWaitSeconds"
        value={config.secondWaitSeconds?.toString() || ""}
        onChange={numericChangeHandler}
        kind="number"
        extraLabel
      />
      <DynamicDropdown
        dropdown={{
          name: "alternativeFundId",
          placeholder: "صندوق پرمعامله",
          extraLabel: true,
        }}
        getData={getCashAssetDropdown}
        onChange={changeHandler}
        value={config.alternativeFundId}
      />
      <div className="w-32 self-center mt-2">
        <Button onClick={submitHandler} text="ثبت" variant="contained" />
      </div>
    </div>
  );
};

export default Settings;
